import { useCallback, useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useQuery } from '@mint-lib/query-client';
import { usePlatforms, type Platform } from '@mint-lib/routing-context';
import { Skeleton, typographyVariants } from '@mint-lib/tw-components';
import { Suggestions } from '@mint-sdk/api-recommendations';
import { useAtom } from 'jotai';

import { selectedSuggestionAtom } from '../../../store/index.js';
import type { PlatformPermission } from '../../../types/platfroms.js';
import { PlatformSection } from '../../atoms/Platforms/PlatformSection.jsx';
import { PlatformPermissions } from '../../atoms/Platforms/PlatformsPermissions.jsx';

const AcceptedPlatformsReadOnly = () => {
  const { t } = useTranslation('@mint-lib/recommendations-circuit');

  const [selectedSuggestion] = useAtom(selectedSuggestionAtom);

  const { data: platforms, isLoading } = useQuery({
    ...Suggestions.getSuggestionPlatforms({
      urlParams: { id: selectedSuggestion?.id as number },
    }),
    enabled: !!selectedSuggestion?.id,
  });

  const { data = [] } = useQuery({
    ...Suggestions.getSuggestionPermissions({
      urlParams: { id: selectedSuggestion?.id as number },
    }),
    enabled: !!selectedSuggestion?.id,
  });

  const { platformByLabel } = usePlatforms();

  const findPlatformByLabel = useCallback(
    (platforms: Suggestions.SuggestionPlatform[] = []): Platform[] => {
      return platforms.reduce<Platform[]>((acc, platform) => {
        const currentPlatform = platformByLabel[platform.name];

        if (currentPlatform) {
          acc.push(currentPlatform);
        }

        return acc;
      }, []);
    },
    [platformByLabel],
  );

  const automaticApplied: Platform[] = useMemo(
    () => findPlatformByLabel(platforms?.integratedPlatforms),
    [findPlatformByLabel, platforms],
  );

  const manuallyApplied: Platform[] = useMemo(
    () => findPlatformByLabel(platforms?.ghostPlatforms),
    [findPlatformByLabel, platforms],
  );

  const getPlatformPermissionByName = useCallback(
    (data: Suggestions.PlatformPermissionResponseType) => {
      return data.reduce<PlatformPermission[]>((acc, platform) => {
        const currentPlatform = platformByLabel[platform.name];

        if (currentPlatform) {
          acc.push({
            ...currentPlatform,
            children: platform.children,
          });
        }

        return acc;
      }, []);
    },
    [platformByLabel],
  );

  const platformsWithPermissions = useMemo<PlatformPermission[]>(
    () => getPlatformPermissionByName(data),
    [data, getPlatformPermissionByName],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <p className={typographyVariants({ variant: 'h4' })}>
          {t('Suggested platforms recap')}
        </p>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <>
            <PlatformSection
              title={t('Automatic applied platform')}
              platforms={automaticApplied}
              ariaLabel={'automatic-applied'}
            />

            <PlatformSection
              title={t('Manually applied platform')}
              platforms={manuallyApplied}
              ariaLabel={'manually-applied'}
            />

            <PlatformPermissions platforms={platformsWithPermissions} />
          </>
        )}
      </div>
    </div>
  );
};

export default AcceptedPlatformsReadOnly;
